import React, { useContext, useState, useRef, useCallback, useEffect } from 'react';
import cn from 'classnames';
import './OfficeSpaceList.scss';

import { OfficeContext } from './Office';
import { getSmartImage } from '../../helpers/helpers';
import { SPACE_TYPE_LABELS, canBookSpace, getSpaceBookingsAtTime, isSpaceAvailable, formatTimeClean } from '../../helpers/office_helpers';
import OfficeSpacePopover from './OfficeSpacePopover';

const OfficeSpaceList = ({ spaces }) => {
  const ctx = useContext(OfficeContext);
  const { selectedSpace, setSelectedSpace, currentUser, selectedDate, selectedTime, selectedEndTime, focusedUserId } = ctx;
  const [popoverPos, setPopoverPos] = useState(null);
  const popoverRef = useRef(null);

  const windowStart = `${selectedDate}T${selectedTime}:00`;
  const windowEnd = selectedEndTime ? `${selectedDate}T${selectedEndTime}:00` : `${selectedDate}T${selectedTime}:30`;

  const openPopover = useCallback(
    (space, el) => {
      const rect = el.getBoundingClientRect();
      const pos = { position: 'fixed', left: Math.min(rect.left, window.innerWidth - 360) };
      if (rect.y > window.innerHeight / 2) pos.bottom = window.innerHeight - rect.top + 8;
      else pos.top = rect.bottom + 8;
      setPopoverPos(pos);
      setSelectedSpace(space);
    },
    [setSelectedSpace]
  );

  useEffect(() => {
    if (!selectedSpace) return;
    const handleClickOutside = e => {
      if (popoverRef.current?.contains(e.target)) return;
      setSelectedSpace(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedSpace, setSelectedSpace]);

  if (!spaces.length) {
    return <div className='office-space-list-outer empty'>No spaces on this floor yet.</div>;
  }

  return (
    <div className='office-space-list-outer'>
      {spaces
        .filter(space => space.type !== 'label')
        .map(space => {
          const activeBookings = getSpaceBookingsAtTime(space, windowStart, windowEnd);
          const hasBookings = activeBookings.length > 0;
          const firstBooking = hasBookings ? activeBookings[0] : null;
          const userCanBook = canBookSpace(currentUser, space);
          const available = isSpaceAvailable(space, windowStart, windowEnd);
          const isSelected = selectedSpace?.id === space.id;
          const allDayBookings = (space.bookings || []).filter(b => b.status === 'confirmed');
          const userInSpace = focusedUserId && allDayBookings.some(b => b.User_id === focusedUserId);
          const userDimmed = focusedUserId && !userInSpace;
          const hasTitle = (space.type === 'desk' || space.type === 'open_seating') && Boolean(firstBooking?.title);
          const img = hasTitle || !firstBooking?.user?.image ? null : getSmartImage(firstBooking.user.image);

          return (
            <div
              key={space.id}
              className={cn('space-card', { selected: isSelected, booked: hasBookings, 'scope-locked': !userCanBook, 'user-dimmed': userDimmed })}
              onClick={e => openPopover(space, e.currentTarget)}
            >
              <div className={cn('status-dot', { available: !hasBookings && userCanBook, booked: hasBookings, locked: !userCanBook })} />
              <div className='card-body'>
                <div className='card-name'>{space.name}</div>
                <div className='card-meta'>
                  <span>{SPACE_TYPE_LABELS[space.type] || space.type}</span>
                  <span className='meta-sep'>·</span>
                  <span>
                    {space.capacity} {space.capacity === 1 ? 'seat' : 'seats'}
                  </span>
                </div>
              </div>
              {hasBookings && (
                <div className='card-booking'>
                  {img && <img src={img} alt='' className='booker-avatar' />}
                  <div className='booker-info'>
                    <span className='booker-name'>{hasTitle ? firstBooking.title : firstBooking.user?.name || 'Booked'}</span>
                    <span className='booker-time'>
                      {formatTimeClean(firstBooking.starts_at)} – {formatTimeClean(firstBooking.ends_at)}
                    </span>
                  </div>
                  {activeBookings.length > 1 && <span className='booking-count'>+{activeBookings.length - 1}</span>}
                </div>
              )}
              {!hasBookings && userCanBook && <div className='card-available-label'>Available</div>}
              {!userCanBook && <div className='card-locked-label'>Restricted</div>}
            </div>
          );
        })}

      {selectedSpace && popoverPos && (
        <div ref={popoverRef}>
          <OfficeSpacePopover space={selectedSpace} onClose={() => setSelectedSpace(null)} positionStyle={popoverPos} />
        </div>
      )}
    </div>
  );
};

export default OfficeSpaceList;
